import ArticleBlockProps from "./ArticleProps.interface";
import styles from "./Article.module.scss";

import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import getImageCrop from "app/media/helpers/getImageCrop";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import IconViewer from "components/IconViewer/IconViewer";
import ImageViewer from "components/ImageViewer/ImageViewer";
import Paragraph from "components/Paragraph/Paragraph";
import React, { Fragment, FunctionComponent } from "react";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useAnchor from "blocks/hooks/useAnchor";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";

const Article: FunctionComponent<ArticleBlockProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    const headlineCaseClass = props.settings.headlineCase ? styles[`case_${props.settings.headlineCase}`] : undefined;

    const imageCrop = getImageCrop(props.content.image?.umbracoFile.crops, props.settings.imageCrop, "Fullscreen (4:3)");
    const imageAspectRatio = getImageAspectRatio(imageCrop, props.content.image?.umbracoWidth, props.content.image?.umbracoHeight);
    const imagePositionClass = props.settings.imagePosition === "Media Left" ? ` ${styles.has_image_left}` : undefined;
    const imageFit = props.settings.imageFit === "Contain" ? "contain" : "cover";

    if (props.settings?.textWidth) {
        wrapperClasses.push(styles[`width_${props.settings.textWidth}`]);
    }

    // Headline string manipulation
    // TODO: Custom hook?
    const splitHeadline = props.content.headline?.split("");
    const firstChar = splitHeadline ? splitHeadline?.[0] : undefined;
    const lastChar = splitHeadline ? splitHeadline.slice(-1)?.[0] : undefined;
    splitHeadline?.shift();
    splitHeadline?.pop();
    const otherChar = splitHeadline?.join("");

    return (
        <section className={combineClasses(styles.wrapper, ...wrapperClasses)} style={{ ...wrapperStyles, ...splitStyles }} id={identifier}>
            <div className={combineClasses(styles.container, ...splitClasses, imagePositionClass, inViewClasses)} ref={inViewElementRef}>

                {/* Content */}
                <div className={styles.inner}>
                    <div className={styles.media}>
                        {/* Image */}
                        {props.content.image ? (
                            <ImageViewer key={props.contentUdi} src={props.content.image.umbracoFile.src} extensionType={props.content.image.umbracoExtension} type={imageFit} crop={imageCrop} focalPoint={props.content.image.umbracoFile.focalPoint} alt={props.content.imageAlternative} aspectRatioX={imageAspectRatio.aspectRatioX} aspectRatioY={imageAspectRatio.aspectRatioY} sourceWidth={props.content.image?.umbracoWidth} sourceHeight={props.content.image?.umbracoHeight} />
                        ) : null}

                        {/* Caption */}
                        {props.content.imageCaption && props.content.imageCaption.length > 0 ? (
                            <div className={styles.caption}>{props.content.imageCaption}</div>
                        ) : null}
                    </div>

                    <div className={styles.content}>
                        {/* Icon */}
                        {props.content.icon ? (
                            <IconViewer className={styles.icon} name={props.content.icon.toLowerCase()} />
                        ) : null}

                        {/* Headline */}
                        {props.content.headline ? (
                            <DynamicHeading key={`head_${props.contentUdi}`} className={combineClasses(styles.headline, headlineCaseClass)} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight}>
                                {/* With Quotes */}
                                {props.settings?.headlineQuotes ? (
                                    <Fragment>
                                        <i className={styles.quote_icon_wrapper}>
                                            {firstChar}
                                            <IconViewer className={styles.quote_icon_start} name="start-quote" />
                                        </i>
                                        <span>{otherChar}</span>
                                        <i className={styles.quote_icon_wrapper}>
                                            {lastChar}
                                            <IconViewer className={styles.quote_icon_end} name="end-quote" />
                                        </i>
                                    </Fragment>
                                ) : null}

                                {/* Without Quotes */}
                                {!props.settings?.headlineQuotes ? (
                                    props.content.headline
                                ) : null}
                            </DynamicHeading>
                        ) : null}

                        {/* Text */}
                        {props.content.bodyText ? (
                            <Paragraph key={`para_${props.contentUdi}`} className={styles.text} innerHTML={props.content.bodyText} />
                        ) : null}
                    </div>
                </div>

                {/* Tagline */}
                {props.content.tagline ? (
                    <div className={styles.tagline}>{props.content.tagline}</div>
                ) : null}

                {/* Buttons */}
                {props.content.buttons && props.content.buttons.length > 0 ? (
                    <div className={styles.buttons}>
                        {props.content.buttons.map((button) => (
                            <Button key={button.contentUdi} to={button.content.link} className={styles.button} variant={button.content.variant} {...button.content}>{button.content.label}</Button>
                        ))}
                    </div>
                ) : null}
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia key={props.contentUdi} {...props.settings.backgroundMedia[0]} />
            ) : null}
        </section>
    );
};

export default Article;